/**
 * Keyboard support for the shared action menus (`ActionMenuPopup`,
 * `ActionDropDown`), following the WAI-ARIA menu-button pattern:
 *
 *  - focus moves to the first enabled item once the popup has opened;
 *  - ArrowDown / ArrowUp move between enabled items (wrapping), Home / End
 *    jump to the first / last;
 *  - Escape closes the menu and returns focus to the trigger;
 *  - Tab closes the menu and returns focus to the trigger *without*
 *    preventing the default, so the browser then moves on from the trigger
 *    to the next (or previous) control — the menu is portaled to the end of
 *    `<body>`, so tabbing out of it would otherwise strand focus there.
 *
 * Enter / Space are left to the browser: items are real `<button>`s, so both
 * fire their `click`.
 *
 * Only the keys handled here are touched. Navigation keys stop propagating so
 * a React-tree ancestor (a clickable card, a grid row) does not act on them
 * too. Escape is only `preventDefault()`ed — `useFocusTrap` reads that as
 * "already handled" and leaves the slide panel underneath open, while any
 * other `document` listener still sees the key.
 */
import { ButtonHandle } from "@progress/kendo-react-buttons";
import React, { useCallback } from "react";

const ENABLED_MENU_ITEM = '[role="menuitem"]:not([aria-disabled="true"])';

const getMenuItems = (menu: HTMLElement | null): HTMLElement[] =>
  menu ? Array.from(menu.querySelectorAll<HTMLElement>(ENABLED_MENU_ITEM)) : [];

export function useActionMenuKeyboard(
  menuRef: React.RefObject<HTMLElement | null>,
  anchor: React.RefObject<ButtonHandle | null>,
  setShow: (show: boolean) => void,
) {
  /** Closes the menu and puts focus back on the button that opened it. */
  const close = useCallback(() => {
    setShow(false);
    anchor.current?.element?.focus();
  }, [anchor, setShow]);

  /** Pass to the Kendo `Popup`'s `onOpen`, which fires once it has rendered. */
  const onOpen = useCallback(() => {
    getMenuItems(menuRef.current)[0]?.focus();
  }, [menuRef]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === "Tab") {
        close();
        return;
      }

      const items = getMenuItems(event.currentTarget);
      if (items.length === 0) return;
      const current = items.indexOf(document.activeElement as HTMLElement);

      let next: number;
      switch (event.key) {
        case "ArrowDown":
          next = current < 0 ? 0 : (current + 1) % items.length;
          break;
        case "ArrowUp":
          next = current <= 0 ? items.length - 1 : current - 1;
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = items.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      event.stopPropagation();
      items[next].focus();
    },
    [close],
  );

  return { close, onOpen, onKeyDown };
}

export default useActionMenuKeyboard;
